import React from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import {
    ResponsiveTable,
    THead,
    TBody,
    TR,
    TH,
    TD,
    EditIcon,
    EllipsisIcon,
    Dropdown,
    AddIcon,
    IconButton,
    ConnectedPlusIcon,
    CheckmarkIcon,
    AlertIcon,
    colors,
    Tooltip,
    Button,
    SkubotSpinner,
} from "@commonsku/styles";
import { CatalogItem, CatalogItemsSortField } from "../../../types";
import type { CatalogItemsFilters } from "./CatalogPage";
import { Select } from "../../helpers";
import { CatalogSearchSupplierFilters } from "./CatalogSearchSupplierFilters";
import {
    CatalogBulkLabel,
    CatalogDeleteAction,
    CatalogDropdownMenuItem,
    CatalogItemsLoadMoreRow,
    CatalogProductActionsBar,
    CatalogProductCell,
    CatalogSupplierCell,
    CatalogRowCheckbox,
    CatalogSelectAllCheckbox,
    EmptyItemStateComponent,
    FilterField,
    FilterLabel,
    PRIMARY_CELL_STYLE,
    ROW_ACTIONS_HEADER_STYLE,
    TABLE_PARENT_PROPS,
    THSorted,
    getCatalogItemsSortOrder,
    optionValueFromSelect,
    useCatalogRowSelection,
} from "./catalogShared";
import { CatalogPublishPopups } from "./CatalogPublishPopups";
import { usePublishCatalogPopup } from "./usePublishCatalogPopup";
import { useEpoUpgradeTracker } from "./useEpoUpgradeTracker";
import { selectCatalogId } from "../../../redux/catalog";
import BulkActionBar from "../../BulkActionBar";

const ActionRowDropdown = styled.div`
    position: relative;

    &&& > span > div:first-of-type {
        padding-left: 8px;
    }

    &&& > span > div:first-of-type > div {
        position: absolute;
        right: 0;
        width: 160px;
        align-items: center;
    }
`;

const StatusCell = styled.div<{ $variant?: "draft" | "setup" }>`
    &&& {
        display: inline-block;
        width: fit-content;
        padding: 4px 8px;
        border-radius: 10000px;
        text-align: center;
        background-color: ${({ $variant }) =>
            $variant === "draft"
                ? colors.secondary2[20]
                : $variant === "setup"
                  ? colors.neutrals[30]
                  : "transparent"};
    }
`;

const UpgradeFailedBadge = styled.span`
    &&& {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        border-radius: 5rem;
        padding: 0.25rem 0.625rem;
        font-size: 0.75rem;
        background-color: ${colors.errors[10]};
        color: ${colors.errors["dark"]};
    }
`;

const CATALOG_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
    { value: "", label: "All" },
    { value: "0", label: "Draft" },
    { value: "1", label: "Setup Required" },
];

const CATALOG_EPO_OPTIONS: Array<{ value: string; label: string }> = [
    { value: "", label: "All" },
    { value: "0", label: "Not Eligible" },
    { value: "1", label: "ePO Enabled" },
    { value: "2", label: "Upgrade for ePO" },
];

const STATUS_DRAFT_TOOLTIP_ID = "status-draft-tip";
const STATUS_SETUP_REQUIRED_TOOLTIP_ID = "status-setup-required-tip";
const EPO_NOT_ELIGIBLE_TOOLTIP_ID = "epo-not-eligible-tip";

function renderTooltips() {
    const sharedProps = {
        place: "top" as const,
        style: {
            padding: "13px 16px",
            borderRadius: 5,
            fontSize: 16,
            zIndex: 1,
            maxWidth: 290,
            textAlign: "left" as const,
        },
        arrowColor: "rgba(18, 57, 82, 0.9)",
    };

    return (
        <>
            <Tooltip id={STATUS_DRAFT_TOOLTIP_ID} {...sharedProps}>
                Fully configured and ready to publish. Use the actions menu to publish this product to the catalog.
            </Tooltip>
            <Tooltip id={STATUS_SETUP_REQUIRED_TOOLTIP_ID} {...sharedProps} clickable>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
                    <span>
                        <strong>Missing required info</strong>
                        <br />
                        Please add external SKUs for all variants. This product cannot be published until setup is complete.
                    </span>
                    <Button variant="primary" size="small">Complete Setup</Button>
                </div>
            </Tooltip>
            <Tooltip id={EPO_NOT_ELIGIBLE_TOOLTIP_ID} {...sharedProps}>
                This product's supplier does not support PromoStandards and cannot be upgraded for ePO.
            </Tooltip>
        </>
    );
}

function getCatalogStatusLabel(status: string): { label: string; variant?: "draft" | "setup" } {
    if (status === "0") {
        return { label: "Draft", variant: "draft" };
    }
    if (Number(status) >= 1) {
        return { label: "Setup Required", variant: "setup" };
    }
    return { label: "—" };
}
export interface ConvertibleProduct {
    promo_api_name: string;
    ps_product_id: string;
    [key: string]: unknown;
}

function findConvertibleProductForItem(
    item: CatalogItem,
    convertibleProducts: ConvertibleProduct[],
): ConvertibleProduct | undefined {
    if (!item.promo_api_name || !item.supplier_code) {
        return undefined;
    }

    return convertibleProducts.find(
        (product) =>
            product.promo_api_name?.toLowerCase() === item.promo_api_name!.toLowerCase() &&
            product.ps_product_id === item.supplier_code,
    );
}

interface EpoStatusCellProps {
    item: CatalogItem;
    convertibleProducts: ConvertibleProduct[];
    convertibleProductsLoading: boolean;
    upgradeStatusLoading?: boolean;
    isUpgrading?: boolean;
    hasFailedUpgrade?: boolean;
    onUpgrade?: (item: CatalogItem, product: ConvertibleProduct) => void;
    onClearUpgradeFailed?: (itemId: string) => void;
}

const EpoStatusCell = ({
    item,
    convertibleProducts,
    convertibleProductsLoading,
    upgradeStatusLoading,
    isUpgrading,
    hasFailedUpgrade,
    onUpgrade,
    onClearUpgradeFailed,
}: EpoStatusCellProps) => {
    const product = findConvertibleProductForItem(item, convertibleProducts);

    if (isUpgrading) {
        return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <SkubotSpinner size="button" skubot={false} spinnerSize={14} containerSize={14} />
                Upgrading…
            </span>
        );
    }

    if (hasFailedUpgrade && product) {
        return (
            <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                <UpgradeFailedBadge>
                    <AlertIcon color={colors.errors["dark"]} />
                    Upgrade failed
                </UpgradeFailedBadge>
                <IconButton
                    Icon={ConnectedPlusIcon}
                    size="tiny"
                    variant="secondary"
                    onClick={() => {
                        onClearUpgradeFailed?.(item.item_id);
                        onUpgrade?.(item, product);
                    }}
                >
                    Retry Upgrade
                </IconButton>
            </span>
        );
    }

    if ((convertibleProductsLoading || upgradeStatusLoading) && item.copied_from !== "ps-products") {
        return <SkubotSpinner style={{ display: "inline-flex", alignItems: "center"}} size="button" skubot={false} spinnerSize={14} containerSize={14} />;
    }

    if (item.copied_from === "ps-products") {
        return <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: colors.green[90] }}><CheckmarkIcon size="small" color={colors.green[90]} /> ePO Enabled</span>;
    }

    if (product) {
        return (
            <IconButton
                Icon={ConnectedPlusIcon}
                size="tiny"
                variant="secondary"
                onClick={() => onUpgrade?.(item, product)}
            >
                Upgrade for ePO
            </IconButton>
        );
    }

    return <span>Not Eligible</span>;
};

interface CatalogUnpublishedViewProps {
    items: CatalogItem[];
    filters: CatalogItemsFilters;
    onFiltersChange: (patch: Partial<CatalogItemsFilters>) => void;
    onApplyFilters: () => void;
    onSortField: (field: CatalogItemsSortField) => void;
    onSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    canDeleteItems: boolean;
    onDeleteItem: (item: CatalogItem) => void;
    hasMore: boolean;
    loadingMore: boolean;
    onLoadMore: () => void;
    onAddProduct: () => void;
    onEditItem: (itemId: string) => void;
}

export const CatalogUnpublishedView = ({
    items,
    filters,
    onFiltersChange,
    onApplyFilters,
    onSortField,
    onSearchKeyDown,
    canDeleteItems,
    onDeleteItem,
    hasMore,
    loadingMore,
    onLoadMore,
    onAddProduct,
    onEditItem,
}: CatalogUnpublishedViewProps) => {
    const { selectedIds, allSelected, someSelected, toggleAll, toggleItem } =
        useCatalogRowSelection(items);

    const convertibleProducts = useSelector(
        (state: any) => state.entities.convertibleProducts?.products ?? [],
    );
    const convertibleProductsLoading = useSelector(
        (state: any) => state.entities.convertibleProducts?.loading === "pending",
    );
    const catalogId = useSelector(selectCatalogId);
    const {
        popupState,
        publishing,
        openPublishFlow,
        openUpgradeFlow,
        closePopup,
        onEpoWarningCancel,
        onEpoWarningUpgrade,
        onPublishWithoutUpgrade,
        onPublishConfirmCancel,
        onPublishConfirm,
        onPublishSuccessDone,
    } = usePublishCatalogPopup();
    const {
        upgradingByItemId,
        upgradeFailedByItemId,
        upgradeStatusLoading,
        onUpgradeQueued,
        clearUpgradeFailed,
    } = useEpoUpgradeTracker({ catalogId, filters });

    const colSpan = 6 - (!canDeleteItems ? 1 : 0);

    const sortOrder = (field: CatalogItemsSortField) =>
        getCatalogItemsSortOrder(filters.sort, field);

    return (
        <>
            <CatalogSearchSupplierFilters
                filters={filters}
                onFiltersChange={onFiltersChange}
                onApplyFilters={onApplyFilters}
                onSearchKeyDown={onSearchKeyDown}
            >
                <FilterField>
                    <FilterLabel>Status</FilterLabel>
                    <Select
                        value={filters.status ?? ""}
                        options={
                            CATALOG_STATUS_OPTIONS as Parameters<typeof Select>[0]["options"]
                        }
                        placeholder="All statuses"
                        isClearable={!!filters.status}
                        onChange={(opt: unknown) => {
                            onFiltersChange({
                                status: optionValueFromSelect(opt) || null,
                            });
                        }}
                    />
                </FilterField>
                <FilterField>
                    <FilterLabel>ePO</FilterLabel>
                    <Select
                        value={filters.is_upgradeable ?? ""}
                        options={
                            CATALOG_EPO_OPTIONS as Parameters<typeof Select>[0]["options"]
                        }
                        placeholder="All"
                        isClearable={!!filters.is_upgradeable}
                        onChange={(opt: unknown) => {
                            onFiltersChange({
                                is_upgradeable: optionValueFromSelect(opt) || null,
                            });
                        }}
                    />
                </FilterField>
            </CatalogSearchSupplierFilters>
            <CatalogProductActionsBar
                action={
                    <IconButton variant="primary" size="large" Icon={AddIcon} iconPosition="left" onClick={onAddProduct}>
                        Product
                    </IconButton>
                }
            >
                Products start as Setup Required, move to Draft once configured, then can be
                Published.
            </CatalogProductActionsBar>
            {renderTooltips()}
            <ResponsiveTable parentProps={TABLE_PARENT_PROPS}>
                <THead>
                    <TR>
                        { canDeleteItems && <CatalogSelectAllCheckbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={toggleAll}
                        />
                        }
                        <THSorted
                            padded
                            clickable
                            order={sortOrder("item_name")}
                            onClick={() => onSortField("item_name")}
                        >
                            Product
                        </THSorted>
                        <THSorted
                            padded
                            clickable
                            order={sortOrder("division_name")}
                            onClick={() => onSortField("division_name")}
                        >
                            Supplier
                        </THSorted>
                        <THSorted
                            padded
                            clickable
                            order={sortOrder("status")}
                            onClick={() => onSortField("status")}
                        >
                            Status
                        </THSorted>
                        <THSorted
                            padded
                            clickable
                            order={sortOrder("is_upgradeable")}
                            onClick={() => onSortField("is_upgradeable")}
                        >
                            ePO
                        </THSorted>
                        <TH style={ROW_ACTIONS_HEADER_STYLE} />
                    </TR>
                </THead>
                <TBody padded>
                    {items.length === 0 ? (
                        <TR>
                            <TD colSpan={colSpan}>
                                <EmptyItemStateComponent />
                            </TD>
                        </TR>
                    ) : items.map((item) => {
                        const isEnabled = item.status === "0" && !convertibleProductsLoading;
                        const status = getCatalogStatusLabel(item.status);
                        const rowActions = [
                            {
                                content: (
                                    <CatalogDropdownMenuItem $disabled={!isEnabled}>
                                        Publish to Catalog
                                    </CatalogDropdownMenuItem>
                                ),
                                onClick: () => {
                                    if (isEnabled) {
                                        openPublishFlow(
                                            item,
                                            findConvertibleProductForItem(item, convertibleProducts),
                                        );
                                    }
                                },
                            },
                        ];
                        if (canDeleteItems) {
                            rowActions.push({
                                content: <CatalogDropdownMenuItem $delete>Delete Product</CatalogDropdownMenuItem>,
                                onClick: () => onDeleteItem(item),
                            });
                        }
                        return (
                            <TR key={item.item_id}>
                                { canDeleteItems && <CatalogRowCheckbox
                                    checked={selectedIds.has(item.item_id)}
                                    onChange={() => toggleItem(item.item_id)}
                                />
                                }
                                <TD padded>
                                    <CatalogProductCell item={item} />
                                </TD>
                                <TD padded style={PRIMARY_CELL_STYLE}>
                                    <CatalogSupplierCell item={item} />
                                </TD>
                                <TD padded style={{ textAlign: "center" }}>
                                    <span
                                        data-tooltip-id={
                                            status.variant === "draft"
                                                ? STATUS_DRAFT_TOOLTIP_ID
                                                : STATUS_SETUP_REQUIRED_TOOLTIP_ID
                                        }
                                    >
                                        <StatusCell $variant={status.variant}>
                                            {status.label}
                                        </StatusCell>
                                    </span>
                                </TD>
                                <TD padded style={{ textAlign: "center" }}>
                                    <span
                                        data-tooltip-id={
                                            !item.promo_api_name && item.copied_from !== "ps-products"
                                                ? EPO_NOT_ELIGIBLE_TOOLTIP_ID
                                                : undefined
                                        }
                                    >
                                        <EpoStatusCell
                                            item={item}
                                            convertibleProducts={convertibleProducts}
                                            convertibleProductsLoading={convertibleProductsLoading}
                                            upgradeStatusLoading={upgradeStatusLoading}
                                            isUpgrading={!!upgradingByItemId[item.item_id]}
                                            hasFailedUpgrade={!!upgradeFailedByItemId[item.item_id]}
                                            onUpgrade={openUpgradeFlow}
                                            onClearUpgradeFailed={clearUpgradeFailed}
                                        />
                                    </span>
                                </TD>
                                <TD padded style={ROW_ACTIONS_HEADER_STYLE}>
                                    <ActionRowDropdown>
                                        <EditIcon onClick={() => onEditItem(item.item_id)} />
                                        <Dropdown
                                            icon={<EllipsisIcon size="medium" />}
                                            items={rowActions}
                                            hideOnMouseLeave={false}
                                            style={{ float: "right" }}
                                        />
                                    </ActionRowDropdown>
                                </TD>
                            </TR>
                        );
                    })}
                    <CatalogItemsLoadMoreRow
                        colSpan={colSpan}
                        hasMore={hasMore}
                        loading={loadingMore}
                        onLoadMore={onLoadMore}
                    />
                </TBody>
            </ResponsiveTable>
            {canDeleteItems && selectedIds.size > 0 && (
                <BulkActionBar
                    selected={items.filter((i) => selectedIds.has(i.item_id)) as any[]}
                    prepareLabel={(sel) => <CatalogBulkLabel count={sel.length} />}
                    actions={[CatalogDeleteAction]}
                />
            )}
            <CatalogPublishPopups
                popupState={popupState}
                publishing={publishing}
                catalogId={catalogId}
                onEpoWarningCancel={onEpoWarningCancel}
                onEpoWarningUpgrade={onEpoWarningUpgrade}
                onPublishWithoutUpgrade={onPublishWithoutUpgrade}
                onUpgradeQueued={onUpgradeQueued}
                onPublishConfirmCancel={onPublishConfirmCancel}
                onPublishConfirm={onPublishConfirm}
                onPublishSuccessDone={onPublishSuccessDone}
                onClose={closePopup}
            />
        </>
    );
};
